// src/lib/auth.ts
import { turso } from '@/database/client';
import { checkRateLimit, getRateLimitResetTime, RateLimitPresets } from './rateLimiter';
import bcrypt from 'bcryptjs';

// Check if database is available
let dbAvailable = true;
let hasCheckedLegacyAdminColumn = false;
let legacyIsAdminColumnExists = false;

async function checkDbAvailable(): Promise<boolean> {
  try {
    // 2-second timeout for database check
    const timeoutPromise = new Promise<never>((_, reject) => 
      setTimeout(() => reject(new Error('DB timeout')), 2000)
    );
    
    const queryPromise = turso.execute('SELECT 1');
    
    const result = await Promise.race([queryPromise, timeoutPromise]);
    return !!(result && result.rows);
  } catch (error) {
    console.warn('[Auth] Database unavailable:', error instanceof Error ? error.message : 'unknown');
    return false;
  }
}

async function ensureLegacyIsAdminColumn(): Promise<boolean> {
  if (hasCheckedLegacyAdminColumn) {
    return legacyIsAdminColumnExists;
  }

  try {
    const result = await turso.execute('PRAGMA table_info(users)');
    if (result?.rows) {
      legacyIsAdminColumnExists = result.rows.some((row: any) => {
        const columnName = (row?.name || row?.Name || row?.NAME || '').toString();
        return columnName === 'is_admin';
      });
    }
  } catch (error) {
    console.warn('[Auth] Unable to inspect users table for is_admin column:', error);
    legacyIsAdminColumnExists = false;
  } finally {
    hasCheckedLegacyAdminColumn = true;
  }

  return legacyIsAdminColumnExists;
}

// LocalStorage fallback for development
const STORAGE_KEYS = {
  USERS: 'whipt_dev_users',
  SESSIONS: 'whipt_dev_sessions'
};

function getStorageUsers(): User[] {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.USERS);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

function saveStorageUsers(users: User[]) {
  localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(users));
}

function getStorageSessions(): Session[] {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.SESSIONS);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

function saveStorageSessions(sessions: Session[]) {
  localStorage.setItem(STORAGE_KEYS.SESSIONS, JSON.stringify(sessions));
}

// Password hashing with bcrypt (12 rounds for security)
async function hashPassword(password: string): Promise<string> {
  const saltRounds = 12; // Higher = more secure but slower
  return await bcrypt.hash(password, saltRounds);
}

async function verifyPassword(password: string, hashedPassword: string): Promise<boolean> {
  return await bcrypt.compare(password, hashedPassword);
}

// Password strength validation
export function validatePasswordStrength(password: string): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters');
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('Must contain at least one uppercase letter');
  }
  if (!/[a-z]/.test(password)) {
    errors.push('Must contain at least one lowercase letter');
  }
  if (!/[0-9]/.test(password)) {
    errors.push('Must contain at least one number');
  }
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push('Must contain at least one special character');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

export type UserRole = 'super' | 'admin' | 'user';

export interface User {
  id: string;
  email: string;
  name: string;
  password_hash: string;
  created_at: string;
  last_active_at: string;
  show_online_status: boolean;
  is_admin: boolean; // Deprecated: use role instead
  role: UserRole;
}

export interface Session {
  token: string;
  user_id: string;
  expires_at: string;
}

export interface AuthResponse {
  success: boolean;
  user?: Omit<User, 'password_hash'>;
  session_token?: string;
  error?: string;
}

// Initialize messaging profile for new user
async function initializeMessagingProfile(
  userId: string,
  name: string,
  email: string
): Promise<void> {
  try {
    await turso.execute({
      sql: `INSERT OR IGNORE INTO messaging_profiles 
            (user_id, display_name, status_message, last_seen, preferences)
            VALUES (?, ?, ?, ?, ?)`,
      args: [
        userId,
        name,
        'Hey there! I am using WHIPT',
        new Date().toISOString(),
        JSON.stringify({ notifications: true, sounds: true })
      ]
    });
    console.log(`[Auth] ✅ Messaging profile initialized for ${email}`);
  } catch (error) {
    console.warn(`[Auth] ⚠️ Failed to initialize messaging profile for ${email}:`, error);
    // Non-critical - user can still use app
  }
}

// Initialize database tables
export async function initAuthTables() {
  try {
    // Check if we're in no-op mode (dev without DB)
    const testResult = await turso.execute('SELECT 1');
    if (!testResult || !testResult.rows) {
      console.warn('[Auth] Running in no-op mode - using localStorage fallback');
      return false;
    }

    // Create users table
    await turso.execute(`
      CREATE TABLE IF NOT EXISTS users (
        id TEXT PRIMARY KEY,
        email TEXT UNIQUE NOT NULL,
        name TEXT NOT NULL,
        password_hash TEXT NOT NULL,
        created_at TEXT NOT NULL,
        last_active_at TEXT NOT NULL,
        show_online_status INTEGER DEFAULT 1,
        role TEXT DEFAULT 'user'
      )
    `);

    // Create sessions table
    await turso.execute(`
      CREATE TABLE IF NOT EXISTS sessions (
        token TEXT PRIMARY KEY,
        user_id TEXT NOT NULL,
        expires_at TEXT NOT NULL,
        FOREIGN KEY (user_id) REFERENCES users(id)
      )
    `);

    // Create entities table (core polymorphic table for all entity types)
    await turso.execute(`
      CREATE TABLE IF NOT EXISTS entities (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        entity_type TEXT NOT NULL,
        data TEXT NOT NULL DEFAULT '{}',
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP
      )
    `);
    await turso.execute(`CREATE INDEX IF NOT EXISTS idx_entities_entity_type ON entities(entity_type)`);

    console.log('[Auth] Database tables initialized');
    return true;
  } catch (error) {
    console.error('[Auth] Failed to initialize tables:', error);
    return false;
  }
}

// Sign up new user
export async function signUp(
  name: string,
  email: string,
  password: string
): Promise<AuthResponse> {
  try {
    // Validate password strength
    const passwordValidation = validatePasswordStrength(password);
    if (!passwordValidation.isValid) {
      return {
        success: false,
        error: `Password requirements not met:\n${passwordValidation.errors.join('\n')}`
      };
    }

    // Rate limiting: 3 sign-up attempts per hour
    const isAllowed = checkRateLimit({
      ...RateLimitPresets.signUp,
      identifier: `signup_${email}`
    });

    if (!isAllowed) {
      const resetTime = getRateLimitResetTime(`signup_${email}`);
      return {
        success: false,
        error: `Too many sign-up attempts. Please try again in ${Math.ceil(resetTime / 60)} minutes.`
      };
    }

    dbAvailable = await checkDbAvailable();

    if (!dbAvailable) {
      // Use localStorage fallback
      console.log('[Auth] Using localStorage fallback for sign up');
      
      const users = getStorageUsers();
      const existing = users.find(u => u.email === email);
      
      if (existing) {
        return { success: false, error: 'Email already registered' };
      }

      const password_hash = await hashPassword(password);
      const userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const now = new Date().toISOString();

      const newUser: User = {
        id: userId,
        email,
        role: 'user' as UserRole,
        name,
        password_hash,
        created_at: now,
        last_active_at: now,
        show_online_status: true
      };

      users.push(newUser);
      saveStorageUsers(users);

      // ALSO add to shared registry so other browsers can see this user
      const allUsersKey = 'whipt_all_registered_users';
      const allUsersData = localStorage.getItem(allUsersKey);
      const allUsers: User[] = allUsersData ? JSON.parse(allUsersData) : [];
      
      // Check if already exists in shared registry
      const existingInRegistry = allUsers.find(u => u.email === email);
      if (!existingInRegistry) {
        allUsers.push(newUser);
        localStorage.setItem(allUsersKey, JSON.stringify(allUsers));
        console.log('[Auth] Added user to shared registry:', email);
      }

      const sessionToken = `session_${Date.now()}_${Math.random().toString(36).substr(2, 16)}`;
      const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

      const sessions = getStorageSessions();
      sessions.push({ token: sessionToken, user_id: userId, expires_at: expiresAt });
      saveStorageSessions(sessions);

      // Also create User entity for admin dropdown (localStorage mode)
      try {
        const { createFlexibleRepository } = await import('@/repositories/flexibleEntityRepository');
        const { userEntityConfig } = await import('@/entities');
        const userRepo = createFlexibleRepository(userEntityConfig);
        
        await userRepo.create({
          userId,
          email,
          name,
          role: 'user',
          created_at: now
        });
        console.log('[Auth] Created User entity for admin dropdown (localStorage mode)');
      } catch (err) {
        console.warn('[Auth] Failed to create User entity (non-critical):', err);
      }

      const { password_hash: _, ...userWithoutPassword } = newUser;
      return {
        success: true,
        user: userWithoutPassword,
        session_token: sessionToken
      };
    }

    // Use database
    // Check if user already exists
    const existing = await turso.execute({
      sql: 'SELECT id FROM users WHERE email = ?',
      args: [email]
    });

    if (existing.rows && existing.rows.length > 0) {
      return { success: false, error: 'Email already registered' };
    }

    // Hash password
    const password_hash = await hashPassword(password);

    // Create user
    const userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const now = new Date().toISOString();

    await turso.execute({
      sql: `INSERT INTO users (id, email, name, password_hash, created_at, last_active_at, show_online_status, role) 
            VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      args: [userId, email, name, password_hash, now, now, 1, 'user']
    });

    // Initialize messaging profile for new user
    await initializeMessagingProfile(userId, name, email);

    // Create session
    const sessionToken = `session_${Date.now()}_${Math.random().toString(36).substr(2, 16)}`;
    const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(); // 30 days

    await turso.execute({
      sql: 'INSERT INTO sessions (token, user_id, expires_at) VALUES (?, ?, ?)',
      args: [sessionToken, userId, expiresAt]
    });

    const user = {
      id: userId,
      email,
      name,
      created_at: now,
      last_active_at: now,
      show_online_status: true,
      is_admin: false,
      role: 'user' as UserRole
    };

    // Also create User entity in Turso for admin dropdown
    try {
      const { createFlexibleRepository } = await import('@/repositories/flexibleEntityRepository');
      const { userEntityConfig } = await import('@/entities');
      const userRepo = createFlexibleRepository(userEntityConfig);
      
      await userRepo.create({
        userId,
        email,
        name,
        role: 'user',
        created_at: now
      });
      console.log('[Auth] Created User entity for admin dropdown');
    } catch (err) {
      console.warn('[Auth] Failed to create User entity (non-critical):', err);
      // Non-fatal - user can still login, just won't appear in admin dropdown yet
    }

    return {
      success: true,
      user,
      session_token: sessionToken
    };
  } catch (error) {
    console.error('[Auth] Sign up error:', error);
    return { success: false, error: 'Failed to create account' };
  }
}

// Sign in existing user
export async function signIn(
  email: string,
  password: string
): Promise<AuthResponse> {
  try {
    // Rate limiting: 5 attempts per 15 minutes per email
    const isAllowed = checkRateLimit({
      ...RateLimitPresets.signIn,
      identifier: `signin_${email}`
    });

    if (!isAllowed) {
      const resetTime = getRateLimitResetTime(`signin_${email}`);
      return {
        success: false,
        error: `Too many sign-in attempts. Please try again in ${Math.ceil(resetTime / 60)} minutes.`
      };
    }

    // Add 10-second timeout to prevent infinite spinning
    const timeoutPromise = new Promise<AuthResponse>((_, reject) => 
      setTimeout(() => reject(new Error('Sign-in timeout')), 10000)
    );

    const signInPromise = (async () => {
      dbAvailable = await checkDbAvailable();

      if (!dbAvailable) {
        // Use localStorage fallback
        console.log('[Auth] Using localStorage fallback for sign in');
        
        const users = getStorageUsers();
        const user = users.find(u => u.email === email);
        
        if (!user) {
          return { success: false, error: 'Invalid email or password' };
        }

        const isValid = await verifyPassword(password, user.password_hash);
        if (!isValid) {
          return { success: false, error: 'Invalid email or password' };
        }

        // Update last active
        const now = new Date().toISOString();
        user.last_active_at = now;
        saveStorageUsers(users);

        const sessionToken = `session_${Date.now()}_${Math.random().toString(36).substr(2, 16)}`;
        const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

        const sessions = getStorageSessions();
        sessions.push({ token: sessionToken, user_id: user.id, expires_at: expiresAt });
        saveStorageSessions(sessions);

        const { password_hash, ...userWithoutPassword } = user;
        return {
          success: true,
          user: { ...userWithoutPassword, last_active_at: now },
          session_token: sessionToken
        };
      }

      // Use database
      console.log('[Auth] Querying database for user:', email);
      // Find user by email
      const result = await turso.execute({
        sql: 'SELECT * FROM users WHERE email = ?',
        args: [email]
      });
      console.log('[Auth] Query complete, rows:', result?.rows?.length || 0);

      if (!result.rows || result.rows.length === 0) {
        return { success: false, error: 'Invalid email or password' };
      }

      const userRow = result.rows[0];
      const user = {
        id: userRow.id as string,
        email: userRow.email as string,
        name: userRow.name as string,
        password_hash: userRow.password_hash as string,
        created_at: userRow.created_at as string,
        last_active_at: userRow.last_active_at as string,
        show_online_status: Boolean(userRow.show_online_status)
      };

      // Verify password
      const isValid = await verifyPassword(password, user.password_hash);
      if (!isValid) {
        return { success: false, error: 'Invalid email or password' };
      }

      // Check if account is active (parental consent for under-13)
      const ageVerificationResult = await turso.execute({
        sql: "SELECT data_value FROM user_data WHERE user_id = ? AND data_type = 'age_verification' LIMIT 1",
        args: [user.id]
      });
      
      if (ageVerificationResult.rows && ageVerificationResult.rows.length > 0) {
        const restrictions = JSON.parse(ageVerificationResult.rows[0].data_value as string);
        if (restrictions.accountActive === false) {
          return { 
            success: false, 
            error: 'Account requires parental consent. Please check your parent\'s email for the approval link.' 
          };
        }
      }

      // Update last active
      const now = new Date().toISOString();
      await turso.execute({
        sql: 'UPDATE users SET last_active_at = ? WHERE id = ?',
        args: [now, user.id]
      });

      // Create new session
      const sessionToken = `session_${Date.now()}_${Math.random().toString(36).substr(2, 16)}`;
      const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(); // 30 days

      await turso.execute({
        sql: 'INSERT INTO sessions (token, user_id, expires_at) VALUES (?, ?, ?)',
        args: [sessionToken, user.id, expiresAt]
      });

      const { password_hash, ...userWithoutPassword } = user;

      return {
        success: true,
        user: { ...userWithoutPassword, last_active_at: now },
        session_token: sessionToken
      };
    })();

    return await Promise.race([signInPromise, timeoutPromise]);
  } catch (error) {
    console.error('[Auth] Sign in error:', error);
    if (error instanceof Error && error.message === 'Sign-in timeout') {
      return { success: false, error: 'Sign-in is taking too long. The database may be waking up. Please try again in a moment.' };
    }
    return { success: false, error: 'Failed to sign in' };
  }
}

// Verify session token
export async function verifySession(token: string): Promise<User | null> {
  try {
    dbAvailable = await checkDbAvailable();

    if (!dbAvailable) {
      console.log('[Auth] DB not available, using localStorage fallback for verifySession');
      // Use localStorage fallback
      const sessions = getStorageSessions();
      const session = sessions.find(s => s.token === token && new Date(s.expires_at) > new Date());
      
      if (!session) {
        console.log('[Auth] No session found in localStorage');
        return null;
      }

      const users = getStorageUsers();
      const user = users.find(u => u.id === session.user_id);
      
      if (user) {
        console.log('[Auth] ✅ Found user in localStorage:', user.email);
      } else {
        console.log('[Auth] ❌ User not found in localStorage');
      }
      
      return user || null;
    }

    // Use database
    console.log('[Auth] Using database for verifySession, token:', token.substring(0, 20) + '...');
    const result = await turso.execute({
      sql: `SELECT u.* FROM users u 
            JOIN sessions s ON u.id = s.user_id 
            WHERE s.token = ? AND s.expires_at > datetime('now')`,
      args: [token]
    });

    if (!result.rows || result.rows.length === 0) {
      console.log('[Auth] ❌ No session found in database, trying localStorage fallback...');
      // Fallback to localStorage if DB query fails/returns nothing
      const sessions = getStorageSessions();
      const session = sessions.find(s => s.token === token && new Date(s.expires_at) > new Date());
      
      if (session) {
        const users = getStorageUsers();
        const user = users.find(u => u.id === session.user_id);
        if (user) {
          console.log('[Auth] ✅ Found user in localStorage fallback:', user.email);
          return user;
        }
      }
      
      return null;
    }

    const userRow = result.rows[0];
    return {
      id: userRow.id as string,
      email: userRow.email as string,
      name: userRow.name as string,
      password_hash: userRow.password_hash as string,
      created_at: userRow.created_at as string,
      last_active_at: userRow.last_active_at as string,
      show_online_status: Boolean(userRow.show_online_status)
    };
  } catch (error) {
    console.error('[Auth] Session verification error:', error);
    return null;
  }
}

// Update user activity (for online presence)
export async function updateUserActivity(userId: string): Promise<void> {
  try {
    dbAvailable = await checkDbAvailable();

    if (!dbAvailable) {
      // Use localStorage fallback
      const users = getStorageUsers();
      const userIndex = users.findIndex(u => u.id === userId);
      if (userIndex !== -1) {
        users[userIndex].last_active_at = new Date().toISOString();
        saveStorageUsers(users);
        
        // ALSO update in shared registry
        const allUsersKey = 'whipt_all_registered_users';
        const allUsersData = localStorage.getItem(allUsersKey);
        const allUsers: User[] = allUsersData ? JSON.parse(allUsersData) : [];
        const sharedUserIndex = allUsers.findIndex(u => u.id === userId);
        if (sharedUserIndex !== -1) {
          allUsers[sharedUserIndex].last_active_at = new Date().toISOString();
          localStorage.setItem(allUsersKey, JSON.stringify(allUsers));
        }
      }
      return;
    }

    // Use database
    const now = new Date().toISOString();
    await turso.execute({
      sql: 'UPDATE users SET last_active_at = ? WHERE id = ?',
      args: [now, userId]
    });
  } catch (error) {
    console.error('[Auth] Update activity error:', error);
  }
}

// Sign out
export async function signOut(token: string): Promise<void> {
  try {
    dbAvailable = await checkDbAvailable();

    if (!dbAvailable) {
      // Use localStorage fallback
      const sessions = getStorageSessions();
      const filtered = sessions.filter(s => s.token !== token);
      saveStorageSessions(filtered);
      return;
    }

    // Use database
    await turso.execute({
      sql: 'DELETE FROM sessions WHERE token = ?',
      args: [token]
    });
  } catch (error) {
    console.error('[Auth] Sign out error:', error);
  }
}

// Get all online users (active in last 5 minutes)
export async function getOnlineUsers(): Promise<Omit<User, 'password_hash'>[]> {
  try {
    dbAvailable = await checkDbAvailable();

    if (!dbAvailable) {
      // WORKAROUND: Get ALL users from localStorage (not just current browser)
      // This includes users from all sign-ups across different browsers
      const allUsersKey = 'whipt_all_registered_users';
      const allUsersData = localStorage.getItem(allUsersKey);
      const allUsers: User[] = allUsersData ? JSON.parse(allUsersData) : [];
      
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
      
      return allUsers
        .filter(u => 
          u.show_online_status && 
          new Date(u.last_active_at) > fiveMinutesAgo
        )
        .map(({ password_hash, ...user }) => user);
    }

    // Use database
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
    
    const result = await turso.execute({
      sql: `SELECT id, email, name, created_at, last_active_at, show_online_status 
            FROM users 
            WHERE last_active_at > ? AND show_online_status = 1
            ORDER BY last_active_at DESC`,
      args: [fiveMinutesAgo]
    });

    if (!result.rows) return [];

    return result.rows.map(row => ({
      id: row.id as string,
      email: row.email as string,
      name: row.name as string,
      created_at: row.created_at as string,
      last_active_at: row.last_active_at as string,
      show_online_status: Boolean(row.show_online_status)
    }));
  } catch (error) {
    console.error('[Auth] Get online users error:', error);
    return [];
  }
}

// Update user role (admin function)
export async function updateUserRole(userId: string, newRole: UserRole): Promise<{ success: boolean; error?: string }> {
  try {
    await turso.execute({
      sql: 'UPDATE users SET role = ? WHERE id = ?',
      args: [newRole, userId]
    });

    // Update legacy is_admin column when it exists (older dashboards still read it)
    if (await ensureLegacyIsAdminColumn()) {
      const isAdmin = newRole === 'super' || newRole === 'admin';
      try {
        await turso.execute({
          sql: 'UPDATE users SET is_admin = ? WHERE id = ?',
          args: [isAdmin ? 1 : 0, userId]
        });
      } catch (error: any) {
        const message = error?.message || error?.toString?.() || '';
        if (message.toLowerCase().includes('no such column')) {
          legacyIsAdminColumnExists = false;
          hasCheckedLegacyAdminColumn = true;
          console.warn('[Auth] Legacy is_admin column missing – skipping update.');
        } else {
          console.warn('[Auth] Failed to update legacy is_admin column:', error);
        }
      }
    }

    console.log(`[Auth] Updated user ${userId} role to ${newRole}`);
    
    return { success: true };
  } catch (error) {
    console.error('[Auth] Update user role error:', error);
    return { success: false, error: 'Failed to update user role' };
  }
}
